import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Shield, Lock } from 'lucide-react';

const Login = () => {
    const { loginWithGoogle, user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    
    // Obtener la ruta a la que intentaba ir el usuario, o ir al dashboard por defecto
    const from = location.state?.from?.pathname || '/campus';

    const [error, setError] = React.useState('');

    // Efecto para redirigir si el usuario ya está autenticado
    React.useEffect(() => {
        if (user) {
            if (user.role === 'Admin') {
                navigate('/admin', { replace: true });
            } else {
                // Lógica de redirección basada en permisos
                if (user.campusEnabled && user.subscriptionEnabled) {
                    navigate('/portal-selection', { replace: true });
                } else if (user.campusEnabled) {
                    navigate('/campus', { replace: true });
                } else if (user.subscriptionEnabled) {
                    navigate('/suscripcion', { replace: true });
                } else {
                    navigate('/no-access', { replace: true });
                }
            }
        }
    }, [user, navigate]);

    const handleLogin = async () => {
        try {
            setError('');
            await loginWithGoogle();
            // La redirección la maneja el useEffect cuando el usuario se actualiza en el contexto
        } catch (err) {
            console.error(err);
            setError('No se pudo iniciar sesión. Intente nuevamente.');
        }
    };

    return (
        <div className="min-h-screen bg-slate-900 flex flex-col items-center justify-center p-4 relative overflow-hidden">
            
            {/* Background Effects */}
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0">
                <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-600/20 rounded-full blur-3xl"></div>
                <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-emerald-600/10 rounded-full blur-3xl"></div>
            </div>

            <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden z-10 relative">
                <div className="p-8 text-center">
                    <div className="w-16 h-16 bg-slate-900 rounded-xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                        <Shield size={32} className="text-blue-500" />
                    </div>
                    
                    <h2 className="text-2xl font-bold text-slate-900 mb-2">Bienvenido al Portal de Clientes</h2>
                    <p className="text-slate-500 mb-8">Acceso seguro para clientes</p>

                    {error && (
                        <div className="mb-4 p-3 bg-red-50 text-red-600 text-sm rounded-lg border border-red-100">
                            {error}
                        </div>
                    )}

                    <button 
                        onClick={handleLogin}
                        className="w-full flex items-center justify-center gap-3 px-6 py-3.5 border border-slate-300 rounded-xl hover:bg-slate-50 transition-all duration-200 group"
                    >
                        <img 
                            src="https://www.svgrepo.com/show/475656/google-color.svg" 
                            alt="Google" 
                            className="w-6 h-6"
                        />
                        <span className="font-medium text-slate-700 group-hover:text-slate-900">
                            Continuar con Google
                        </span>
                    </button>

                    <div className="mt-8 flex items-center justify-center gap-2 text-xs text-slate-400">
                        <Lock size={12} />
                        <span>Conexión encriptada de extremo a extremo</span>
                    </div>
                </div>
                
                <div className="bg-slate-50 px-8 py-4 border-t border-slate-100 text-center">
                    <p className="text-xs text-slate-400">
                        Al ingresar, usted acepta nuestros <a href="#" className="text-blue-600 hover:underline">Términos de Servicio</a> y <a href="#" className="text-blue-600 hover:underline">Política de Privacidad</a>.
                    </p>
                </div>
            </div>

            <div className="mt-8 text-slate-500 text-sm">
                &copy; 2025 X Project S.A. Todos los derechos reservados.
            </div>
        </div>
    );
};

export default Login;